import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import api from '../api/client'

const REASONS = [
  'Membership Information',
  'General Questions',
  'Website Feedback',
  'Report Found Bird',
  'Please Send AU Newsletter',
  'Please Send e-Updates',
  'Other',
] as const

const OFFICE = {
  phone: '(405) 848-5801',
  email: 'arpuoffice@pigeon.org',
  address: 'PO Box 18465, Oklahoma City, OK 73154-0465',
}

export function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', reason: '', message: '', hp: '' })
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState('')

  const update = (field: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim() || !form.reason || !form.message.trim()) {
      setError('Please fill in your name, email, reason, and message.')
      setStatus('error')
      return
    }
    setStatus('sending')
    setError('')
    try {
      await api.post('/contact', form)
      setStatus('sent')
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Sorry, we could not send your message right now. Please call the AU office.'
      setError(msg)
      setStatus('error')
    }
  }

  const inputClass =
    'w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm'

  return (
    <>
      <Helmet>
        <title>Contact Us | American Racing Pigeon Union</title>
        <meta
          name="description"
          content="Contact the American Racing Pigeon Union office with questions about membership, bands, found birds, and more."
        />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <h1 className="font-heading text-3xl font-bold text-text mb-2">Contact Us</h1>
        <p className="text-text-muted mb-8">
          Questions about membership, bands, a found bird, or anything else? Send us a message and the AU
          office will get back to you.
        </p>

        <div className="grid gap-8 md:grid-cols-[1fr_280px]">
          {/* Form */}
          <div className="border border-border rounded-lg p-6 bg-surface">
            {status === 'sent' ? (
              <div className="py-8 text-center">
                <h2 className="font-heading text-xl font-semibold text-text mb-2">Message sent!</h2>
                <p className="text-text-muted">
                  Thank you for contacting the American Racing Pigeon Union. The office will reply to your
                  email as soon as possible.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Name *</label>
                    <input className={inputClass} value={form.name} onChange={(e) => update('name', e.target.value)} maxLength={120} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Email *</label>
                    <input className={inputClass} type="email" value={form.email} onChange={(e) => update('email', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Phone</label>
                    <input className={inputClass} type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} maxLength={40} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Reason to Contact *</label>
                    <select className={inputClass} value={form.reason} onChange={(e) => update('reason', e.target.value)}>
                      <option value="">Please choose one</option>
                      {REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Honeypot — hidden from real users */}
                  <input
                    type="text"
                    value={form.hp}
                    onChange={(e) => update('hp', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    name="website"
                  />
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-text mb-1">Message *</label>
                    <textarea
                      className={`${inputClass} min-h-32`}
                      value={form.message}
                      onChange={(e) => update('message', e.target.value)}
                      maxLength={5000}
                      rows={6}
                    />
                  </div>
                </div>

                {status === 'error' && error && <p className="text-error text-sm mt-3">{error}</p>}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="mt-4 px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors disabled:opacity-50"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* Office info */}
          <div className="border border-border rounded-lg p-6 bg-surface h-fit">
            <h2 className="font-heading font-semibold text-text mb-4">AU Office</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="font-medium text-text">Phone</dt>
                <dd className="text-text-muted">
                  <a href="tel:+14058485801" className="hover:text-primary">
                    {OFFICE.phone}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-text">Email</dt>
                <dd className="text-text-muted">
                  <a href={`mailto:${OFFICE.email}`} className="hover:text-primary">
                    {OFFICE.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-text">Mailing Address</dt>
                <dd className="text-text-muted">{OFFICE.address}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </>
  )
}
